import UnitManagement from "./UnitManagement";
import React from "react";
import {FLOOR_TYPES} from "../../constants/floorTypes";
import {BUILDING_TYPE} from "./type/BuildingType";

const BuildingDetail = ({selectedBuilding, detailDemandPoint, detailLoading, switchToView}) => {
    if (!selectedBuilding) {
      return (
        <div className="container mx-auto p-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Gebäude Details</h2>
            <button onClick={() => switchToView('buildings')} className="btn-cancel">Zurück</button>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">⚠️ Kein Gebäude ausgewählt.</p>
            <p className="text-yellow-700 text-sm mt-1">Bitte wählen Sie ein Gebäude aus der Liste aus.</p>
          </div>
        </div>
      );
    }

    // Use fresh data from GET /api/demand-points/{id}; fall back to selectedBuilding while loading.
    // Handle both snake_case and camelCase field names from backend.
    const dp = detailDemandPoint ?? selectedBuilding._demandPoint ?? {};

    // postal_address may come as dp.postal_address, dp.postalAddress, or inline on dp itself
    const rawAddr = dp.postal_address ?? dp.postalAddress ?? null;
    const addr = rawAddr ?? selectedBuilding.address ?? {};

    // location may be nested or inline
    const rawLoc = dp.location ?? null;
    const loc = rawLoc ?? selectedBuilding.location ?? {};

    // building data
    const bld = dp.building ?? selectedBuilding ?? {};
    const floors = bld.floors ?? selectedBuilding.floors ?? [];
    const actualFloors = floors.filter(f => f.floorType !== FLOOR_TYPES.UNKNOWN.value);

    const buildingTypeLabel = BUILDING_TYPE[bld.type]?.label || '—';

    const fmtDate = (val) => val
      ? new Date(val).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '—';

    return (
      <div className="container mx-auto p-4 max-w-4xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Gebäude Details</h2>
            {(addr.street || addr.city) && (
              <p className="text-gray-500 text-sm mt-0.5">
                {addr.street} {addr.house_number ?? ''}{addr.house_number_suffix || ''}, {addr.zip_code} {addr.city}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => switchToView('building-form')} className="btn-primary px-4 py-2">
              Bearbeiten
            </button>
            <button onClick={() => switchToView('buildings')} className="btn-cancel">Zurück</button>
          </div>
        </div>

        {/* Loading indicator */}
        {detailLoading && (
          <div className="flex items-center gap-2 text-sm text-orange-600 mb-4">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
            Lade aktuelle Daten…
          </div>
        )}

        <div className="space-y-4">

          {/* ── Postal Address ─────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Adresse</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Straße</p>
                <p className="font-medium">{addr.street || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Hausnummer</p>
                <p className="font-medium">
                  {addr.houseNumber ?? '—'}{addr.houseNumberSuffix ? ` ${addr.houseNumberSuffix}` : ''}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">PLZ</p>
                <p className="font-medium">{addr.zipCode || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Ort</p>
                <p className="font-medium">{addr.city || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Ortsteil</p>
                <p className="font-medium">{addr.district || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Bundesland</p>
                <p className="font-medium">{addr.federalState || '—'}</p>
              </div>
              {addr.external_id && (
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs mb-0.5">Adress-UUID</p>
                  <p className="font-medium font-mono text-xs break-all">{addr.externalId}</p>
                </div>
              )}
              {addr.version != null && (
                <div>
                  <p className="text-gray-500 text-xs mb-0.5">Version</p>
                  <p className="font-medium">{addr.version}</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Demand Point ───────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Demand Point</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-gray-500 text-xs mb-0.5">ID</p>
                <p className="font-medium">{dp.id ?? dp.externalId ?? dp.external_id ?? '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Typ</p>
                <p className="font-medium">{buildingTypeLabel}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">External ID</p>
                <p className="font-medium font-mono text-xs break-all">{dp.externalId ?? dp.external_id ?? '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">UUID</p>
                <p className="font-medium font-mono text-xs break-all">{dp.externalUuid ?? dp.external_uuid ?? '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">GPS-Koordinaten</p>
                <p className="font-medium">
                  {loc.latitude  != null ? Number(loc.latitude).toFixed(6)  : '—'},&nbsp;
                  {loc.longitude != null ? Number(loc.longitude).toFixed(6) : '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Version</p>
                <p className="font-medium">{dp.version ?? '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Erstanlage</p>
                <p className="font-medium">{fmtDate(dp.created_at)}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Letzte Änderung</p>
                <p className="font-medium">{fmtDate(dp.updated_at)}</p>
              </div>
            </div>
          </div>

          {/* ── Building ───────────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Gebäude</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {bld.name && (
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs mb-0.5">Name</p>
                  <p className="font-medium">{bld.name}</p>
                </div>
              )}
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Ebenen gesamt</p>
                <p className="font-medium">{floors.length || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Wohneinheiten</p>
                <p className="font-medium">
                  {actualFloors.reduce((s, f) => s + (f.units?.livingUnitCount || 0), 0)}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-0.5">Gewerbeeinheiten</p>
                <p className="font-medium">
                  {actualFloors.reduce((s, f) => s + (f.units?.commercialUnitCount || 0), 0)}
                </p>
              </div>
            </div>

            {/* Floor list */}
            {actualFloors.length > 0 && (
              <div className="border-t border-gray-100 px-4 pb-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-3 mb-2">Etagen</p>
                <div className="space-y-1">
                  {actualFloors.map((floor, i) => (
                    <div key={`actualFloor${i}`} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm">
                      <span className="font-medium text-gray-700">
                        {FLOOR_TYPES[floor.floorType].label}{floor.floorNumber != null ? ` (${floor.floorNumber})` : ''}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {floor.units?.livingUnitCount ?? 0} WE &nbsp;·&nbsp; {floor.units?.commercialUnitCount ?? 0} GE
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Access notes ───────────────────────────────────── */}
          {(bld.access_notes || selectedBuilding.access_notes) && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Zugangshinweise</h3>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {bld.access_notes || selectedBuilding.access_notes}
                </p>
              </div>
            </div>
          )}

          {/* ── Unit Management ────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Einheiten-Verwaltung</h3>
            </div>
            <div className="p-4">
              <UnitManagement building={selectedBuilding} />
            </div>
          </div>

        </div>
      </div>
    );
  };

export default BuildingDetail;